import { CodedStatusMessage } from '@gfe/core/models/coded-status-message.model';

import { DeployStatusCode } from './plan.model';
import { hasAdminOrAbove, hasRootRole } from './user-role.constants';

export enum DeployChannelType {
  EMAIL = 'EMAIL',
  MULTIPLE_EMAILS = 'MULTIPLE_EMAILS',
  WEBHOOK = 'WEBHOOK',
  GEDMOUV = 'GEDMOUV',
  GAEL = 'GAEL'
}

/**
 * All three of `allowRedeploy`, `entityType` and `supportsDeployInSteps` are static per
 * configuration, so the whole deploy page draws its controls from the list call alone.
 */
export interface DeployPlanConfigItem {
  id: string;
  name: string;
  channel: DeployChannelType;
  allowRedeploy: boolean;
  /** What this configuration's message uids are keyed by; null when nothing can answer. */
  entityType: string | null;
  /** Whether a deploy body may carry `uids` at all. */
  supportsDeployInSteps: boolean;
  /**
   * Whether this configuration may deploy at all. A disabled one is still listed — it is not a
   * soft delete — so the deploy page has to show it without offering it.
   */
  isEnabled: boolean;
}

export interface DeployPlanConfigListResponse {
  items: DeployPlanConfigItem[];
}

/** Every channel's detail carries the same identity and deploy capabilities. */
interface DeployConfigDetailBase {
  id: string;
  allowRedeploy: boolean;
  /** What this configuration's message uids are keyed by; null when nothing can answer. */
  entityType: string | null;
  /** Whether a deploy body may carry `uids` at all. */
  supportsDeployInSteps: boolean;
  /** Whether this configuration may deploy at all. */
  isEnabled: boolean;
  name: string;
  /**
   * The configuration's own dataspace, which scopes the secret picker: the update use case
   * asserts the chosen secret exists in *this* dataspace, not the one the route came from.
   */
  dataSpaceId: number;
}

export interface EmailDeployConfigDetail extends DeployConfigDetailBase {
  channel: DeployChannelType.EMAIL;
  to: string | string[];
  subject: string;
  /** Optional raw text rendered before the report body. May be absent/null when never set. */
  messageBefore?: string;
  /** Optional raw text rendered after the report body. May be absent/null when never set. */
  messageAfter?: string;
}

export interface MultipleEmailsDeployConfigDetail extends DeployConfigDetailBase {
  channel: DeployChannelType.MULTIPLE_EMAILS;
  subject: string;
  /** Optional raw text rendered before the report body. May be absent/null when never set. */
  messageBefore?: string;
  /** Optional raw text rendered after the report body. May be absent/null when never set. */
  messageAfter?: string;
}

export interface WebhookDeployConfigDetail extends DeployConfigDetailBase {
  channel: DeployChannelType.WEBHOOK;
  url: string;
}

export interface GedmouvDeployConfigDetail extends DeployConfigDetailBase {
  channel: DeployChannelType.GEDMOUV;
  /** Key of the stored secret holding the GEDMOUV auth token (not the token itself). */
  secret: string;
  url: string;
}

export interface GaelDeployConfigDetail extends DeployConfigDetailBase {
  channel: DeployChannelType.GAEL;
  /** SFTP username. */
  login: string;
  /** Key of the stored secret holding the SFTP password (never the password itself). */
  secret: string;
  /** SFTP host or IP. */
  ip: string;
  /** SFTP port (positive integer, e.g. 22). */
  port: number;
  /** Remote directory the file is uploaded into (e.g. /uploads/gael). */
  directory: string;
  /** Filename template — supports reserved {date}/{time}/{year}/{month}/{day}/{hour}/{minutes}/{seconds}/{plan_id} tokens. */
  filename: string;
}

export type DeployPlanConfigDetail =
  | EmailDeployConfigDetail
  | MultipleEmailsDeployConfigDetail
  | WebhookDeployConfigDetail
  | GedmouvDeployConfigDetail
  | GaelDeployConfigDetail;

/**
 * A preprocessor the deploy pipeline runs before the channel. `name` is not narrowed to the
 * keys this build knows: a stored configuration may name a processor added since, and losing
 * it on a round-trip would quietly change what the channel sends.
 */
export interface DeployPreprocessorRef {
  name: string;
  config?: Record<string, unknown>;
}

/**
 * What the ROOT-only `/admin` surface returns — the stored row, rather than the channel-shaped
 * projection the user-facing detail gives. Its list endpoint returns this same shape per item.
 */
export interface DeployPlanConfigAdminDetail {
  id: string;
  name: string;
  dataSpaceId: number;
  channel: DeployChannelType;
  channelConfig: Record<string, unknown>;
  preprocessors: DeployPreprocessorRef[];
  templateContent: string | null;
}

export interface DeployPlanConfigAdminListResponse {
  items: DeployPlanConfigAdminDetail[];
}

/**
 * A new configuration. `allowRedeploy` is absent because the create endpoint does not accept
 * it — a new configuration starts at the stored default and the flag is set by a later edit.
 */
export interface DeployChannelConfigDraft {
  name: string;
  dataSpaceId: number;
  channel: DeployChannelType;
  channelConfig: Record<string, unknown>;
  preprocessors: DeployPreprocessorRef[];
  templateContent: string | null;
}

export interface DeployChannelConfigCapabilities {
  canCreate: boolean;
  canDelete: boolean;
  canRename: boolean;
  /** Preprocessors and template content: stored fields only the `/admin` surface exposes. */
  canSeeAdvanced: boolean;
  canToggleEnabled: boolean;
}

/**
 * Which channel-configuration actions a role may perform, mirroring what the two back-end
 * surfaces accept: creating and deleting exist only on the ROOT-only `/admin` controller,
 * while listing, editing and renaming go through the user-facing one.
 */
export function deployChannelConfigCapabilities(
  role: string | undefined
): DeployChannelConfigCapabilities {
  const isRoot = hasRootRole(role);
  return {
    canCreate: isRoot,
    canDelete: isRoot,
    canRename: hasAdminOrAbove(role),
    canSeeAdvanced: isRoot,
    // Coincides with `canRename` for a different reason: renaming is ADMIN because this page is
    // ADMIN-gated though its route accepts USER, while the on/off route itself demands ADMIN.
    canToggleEnabled: hasAdminOrAbove(role)
  };
}

export enum DeployContentKind {
  MESSAGES = 'messages',
  VIEW = 'view'
}

export interface DeploySendAttempt {
  sentAt: string;
  success: boolean;
  recipients: string[];
  error: string | null;
  /** GedMouv tour id — the only handle an operator has to delete a duplicate tour. */
  externalId: string | null;
}

export interface DeployMessage {
  /** Business uid of the entity the message was split on; the key sent back to deploy. */
  uid: string;
  label: string;
  recipients: ContactEmailRecipient[];
  /** Channel-defined, and not `recipients.length > 0` — a GedMouv message has none. */
  isValid: boolean;
  warnings: CodedStatusMessage[];
  /**
   * The rendered body, `null` when the call asked for no content. Not always a string:
   * MULTIPLE_EMAILS sends `{ subject, html }` and a webhook with `parse_to_json` sends
   * parsed JSON, so it must go through `resolveDeployView` before being displayed.
   */
  view: unknown;
  /** Every attempt, oldest first. `[]` means never attempted. */
  sends: DeploySendAttempt[];
}

export interface DeployMessagesResponse {
  contentKind: DeployContentKind.MESSAGES;
  contentType: string;
  entityType: string | null;
  content: {
    /** Configuration-level only, so usually empty; it exists for the `messages: []` case. */
    warnings: CodedStatusMessage[];
    messages: DeployMessage[];
  };
}

export interface DeployViewResponse {
  contentKind: DeployContentKind.VIEW;
  contentType: string;
  entityType: string | null;
  content: {
    isValid: boolean;
    warnings: CodedStatusMessage[];
    /** `null` means no restriction; `[]` means nothing is pickable. Not the same state. */
    validUids: string[] | null;
    /** Same wire shapes as `DeployMessage.view`; resolve it before displaying it. */
    view: unknown;
  };
}

/** Discriminated on `contentKind`; never sniff for the presence of `messages`. */
export type DeployPlanMessagesResponse = DeployMessagesResponse | DeployViewResponse;

/**
 * Whether a channel counts individual messages at all. It is what tells the two meanings of a
 * null `totalMessages` apart: a `SINGLE_PAYLOAD` channel has no ratio to show, while a
 * `PER_MESSAGE` one whose denominator nobody recorded still knows how much went out.
 */
export enum DeployCountKind {
  PER_MESSAGE = 'PER_MESSAGE',
  SINGLE_PAYLOAD = 'SINGLE_PAYLOAD'
}

/**
 * How much of what a configuration had to send is out. The numbers are cumulative per
 * (plan, configuration) rather than per deploy, so a channel deployed in steps reports the
 * whole plan's progress instead of the last step's tally.
 */
export interface DeployChannelStats {
  countKind: DeployCountKind;
  /**
   * Every candidate message, undeliverable ones included — so a channel that mailed 73 of
   * 111 routes reports 111 rather than the useless 73. `null` both for a `SINGLE_PAYLOAD`
   * channel, which has no per-message notion, and for a `PER_MESSAGE` deploy recorded before
   * GBE kept the denominator — which is unrecoverable, being known only at send time.
   */
  totalMessages: number | null;
  sentCount: number;
  /** Messages attempted and still not out. Never overlaps `sentCount`. */
  failedCount: number;
}

/** One configuration's latest deploy outcome for a plan, from `/deploy-summary`. */
export interface DeployPlanSummaryItem extends DeployChannelStats {
  configId: string;
  name: string;
  channel: DeployChannelType;
  status: DeployStatusCode;
  /** Newest successful send, across every deploy; `null` when nothing ever went out. */
  lastSentAt: string | null;
  /** When the latest deploy reached `status`. */
  updatedAt: string;
}

/**
 * A configuration never deployed for this plan is **absent** from `items` rather than
 * reported with zeroes, so "never deployed" stays distinguishable from "nothing got out".
 */
export interface DeployPlanSummaryResponse {
  items: DeployPlanSummaryItem[];
}

/**
 * Send history keyed by message uid, attempts oldest first. A uid with no attempts is absent,
 * and the response covers the whole (plan, configuration) history — so it is authoritative
 * for every message, not a patch to apply over what a caller already holds.
 */
export interface DeployMessageSendsResponse {
  sends: Record<string, DeploySendAttempt[]>;
}

/**
 * A single resolved recipient of a deploy message.
 * `name` may be absent when only an e-mail address is known.
 */
export interface ContactEmailRecipient {
  name?: string;
  email: string;
}
